/**
 * #273 -- the lead form is the Contacts form.
 *
 * A lead becomes a customer, so anything the Contacts form asks for is worth
 * capturing while the lead is still a lead: converting should be a copy, not a
 * re-entry. The per-customer_type field schema is IMPORTED from
 * `@/utils/customerBasicInfo` -- the same module CustomerFormPage renders
 * from -- so the two forms cannot drift apart.
 *
 * The fields split two ways on submit: those with a `Lead` column go at the
 * top level of the payload, the rest go under `contact_details`, which the
 * backend stores on `lead_data["contact"]` and copies onto the Customer row at
 * conversion (`apps/leads/conversion.py`).
 */

import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select, ContentLoader, BackButton } from '@/components/common';
import { AddressFields } from '@/components/forms/AddressFields';
import { useLead, useCreateLead, useUpdateLead, useLeadSources } from '@/hooks/useLeads';
import {
  phoneSchema,
  digitsOnlyOnChange,
  nameKeepOnChange,
  registrationOnChange,
  optionalEmailSchema,
  allowUnchangedEmails,
} from '@/utils/validation';
import { personNameProblem } from '@/utils/format';
import {
  customerTypeOptions,
  getBasicFieldSpecs,
  getIdentityFieldSpecs,
  isCompanyNameType,
  segmentOptions,
  type CustomerFieldKey,
  type CustomerFieldSpec,
} from '@/utils/customerBasicInfo';
import type { LeadContactDetails, LeadWritePayload } from '@/services/leads';

// Of the shared Contacts schema, salutation / first_name / last_name /
// company_name / industry have a column of their own on `Lead`; everything
// else in the schema travels in `contact_details` (CONTACT_DETAIL_KEYS below).

/** An API timestamp -> the `YYYY-MM-DDTHH:mm` a `datetime-local` input wants,
 *  in the viewer's own timezone. Returns '' for anything unparseable rather
 *  than seeding the field with "Invalid Date". */
function toDateTimeLocal(value?: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

/** The reverse: the input's local wall-clock value -> a full ISO instant, so
 *  the server stores the moment the user meant and not a naive string whose
 *  timezone the API has to guess. */
function fromDateTimeLocal(value: string): string | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

const leadSchema = z
  .object({
    // Customer Type drives which of the schema fields render.
    customer_type: z.string().min(1, 'Customer type is required'),
    segment: z.string().optional(),
    // Contact (always shown)
    phone: phoneSchema,
    // Optional, unlike the old form: a walk-in lead often leaves a number only.
    email: optionalEmailSchema,
    alternate_phone: z.string().optional(),
    whatsapp_number: z.string().optional(),
    designation: z.string().optional(),
    // Lead columns from the shared schema
    salutation: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    company_name: z.string().optional(),
    industry: z.string().optional(),
    // contact_details, from the shared schema
    middle_name: z.string().optional(),
    date_of_birth: z.string().optional(),
    gender: z.string().optional(),
    marital_status: z.string().optional(),
    occupation: z.string().optional(),
    annual_income: z.string().optional(),
    company_type: z.string().optional(),
    incorporation_date: z.string().optional(),
    cin_number: z.string().optional(),
    nature_of_business: z.string().optional(),
    annual_turnover: z.string().optional(),
    num_employees: z.string().optional(),
    website: z.string().url('Invalid URL').optional().or(z.literal('')),
    pan_number: z.string().optional(),
    aadhaar_number: z.string().optional(),
    gst_number: z.string().optional(),
    passport_number: z.string().optional(),
    driving_license: z.string().optional(),
    // Address
    address_line1: z.string().optional(),
    address_line2: z.string().optional(),
    landmark: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pincode: z.string().optional(),
    // Lead details
    source: z.string().min(1, 'Source is required'),
    campaign: z.string().optional(),
    product_interest: z.string().min(1, 'Product interest is required'),
    estimated_premium: z.string().optional(),
    priority: z.enum(['low', 'medium', 'high', 'hot']).optional(),
    // Item 9. Both columns have existed since `0002` and
    // `LeadUpdateSerializer` already accepted them; this form simply never
    // asked, so `next_follow_up` on the detail page read '-' for every lead
    // that was ever captured.
    next_follow_up: z.string().optional(),
    follow_up_notes: z.string().optional(),
    notes: z.string().optional(),
    // Insurance-type-specific detail. Shown only when Product Interest is
    // Motor, and all optional: a lead captured on a phone call is often
    // half-known, and refusing to save it because the variant is missing
    // loses the lead outright.
    registration_number: z.string().optional(),
    vehicle_type: z.string().optional(),
    vehicle_make: z.string().optional(),
    vehicle_model: z.string().optional(),
    vehicle_variant: z.string().optional(),
    vehicle_fuel_type: z.string().optional(),
    manufacturing_year: z.string().optional(),
    is_new_vehicle: z.string().optional(),
    existing_policy_expiry: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Mirrors CustomerFormPage: a corporate lead is named by its company, an
    // individual by its person, a proprietorship by both.
    if (isCompanyNameType(data.customer_type) && !data.company_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['company_name'],
        message: 'Company name is required',
      });
    }
    if (data.customer_type !== 'corporate') {
      // Visible message instead of silently dropping digits (DQ-640).
      for (const key of ['first_name', 'middle_name', 'last_name'] as const) {
        const problem = personNameProblem(data[key]);
        if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: problem });
      }
    }
    if (data.customer_type !== 'corporate' && !data.first_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['first_name'],
        message: 'First name is required',
      });
    }
  });

type LeadFormData = z.infer<typeof leadSchema>;
/** The dynamic (schema-driven) half of the form. */
type SchemaFieldName = Extract<CustomerFieldKey, keyof LeadFormData>;

const vehicleTypeOptions = [
  { value: '', label: 'Select vehicle type' },
  { value: 'two_wheeler', label: 'Two Wheeler' },
  { value: 'private_car', label: 'Private Car' },
  { value: 'goods_carrier', label: 'Goods Carrying Vehicle' },
  { value: 'passenger', label: 'Passenger Carrying Vehicle' },
  { value: 'commercial', label: 'Other Commercial Vehicle' },
];

const fuelTypeOptions = [
  { value: '', label: 'Select fuel type' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'cng', label: 'CNG' },
  { value: 'lpg', label: 'LPG' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
];

const newVehicleOptions = [
  { value: '', label: '-' },
  { value: 'yes', label: 'Yes — new vehicle' },
  { value: 'no', label: 'No — existing vehicle' },
];

const productInterestOptions = [
  { value: 'motor', label: 'Motor Insurance' },
  { value: 'health', label: 'Health Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'travel', label: 'Travel Insurance' },
  { value: 'home', label: 'Home Insurance' },
  { value: 'business', label: 'Business Insurance' },
  { value: 'marine', label: 'Marine Insurance' },
  { value: 'fire', label: 'Fire Insurance' },
  { value: 'cyber', label: 'Cyber Insurance' },
  { value: 'pa', label: 'Personal Accident' },
  { value: 'other', label: 'Other' },
];

// The backend vocabulary is low|medium|high|hot (Lead.PRIORITY_CHOICES). The
// old form offered "Urgent", which no Lead row could ever hold.
const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'hot', label: 'Hot' },
];

/** Keys carried under `contact_details` rather than as Lead columns. */
const CONTACT_DETAIL_KEYS = [
  'customer_type',
  'segment',
  'middle_name',
  'date_of_birth',
  'gender',
  'marital_status',
  'occupation',
  'annual_income',
  'company_type',
  'incorporation_date',
  'cin_number',
  'nature_of_business',
  'annual_turnover',
  'num_employees',
  'website',
  'pan_number',
  'aadhaar_number',
  'gst_number',
  'passport_number',
  'driving_license',
  'address_line1',
  'address_line2',
  'landmark',
] as const;

export function LeadFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const { data: lead, isLoading: leadLoading } = useLead(id!);
  const { data: sources } = useLeadSources();
  const createMutation = useCreateLead();
  const updateMutation = useUpdateLead();

  const defaultSourceOptions = [
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'walk_in', label: 'Walk-in' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'email', label: 'Email' },
    { value: 'partner', label: 'Partner' },
    { value: 'advertisement', label: 'Advertisement' },
    { value: 'other', label: 'Other' },
  ];
  const sourceOptions =
    sources && sources.length > 0
      ? sources.map((s) => ({ value: s.id, label: s.name }))
      : defaultSourceOptions;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: allowUnchangedEmails(zodResolver(leadSchema), () => ({ email: lead?.email })),
    defaultValues: {
      customer_type: 'individual',
      priority: 'medium',
      source: '',
    },
  });

  const customerType = watch('customer_type');
  // The Vehicle card is motor-only: a health or life lead should not be asked
  // for a registration number.
  const isMotorLead = watch('product_interest') === 'motor';

  useEffect(() => {
    if (!lead) return;
    const details = (lead.contact_details ?? {}) as LeadContactDetails;
    reset({
      customer_type: details.customer_type || 'individual',
      segment: details.segment || '',
      salutation: lead.salutation || '',
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      company_name: lead.company_name || '',
      industry: lead.industry || '',
      designation: lead.designation || '',
      phone: lead.phone || '',
      email: lead.email || '',
      alternate_phone: lead.alternate_phone || '',
      whatsapp_number: lead.whatsapp_number || '',
      middle_name: details.middle_name || '',
      date_of_birth: details.date_of_birth || '',
      gender: details.gender || '',
      marital_status: details.marital_status || '',
      occupation: details.occupation || '',
      annual_income: details.annual_income ?? '',
      company_type: details.company_type || '',
      incorporation_date: details.incorporation_date || '',
      cin_number: details.cin_number || '',
      nature_of_business: details.nature_of_business || '',
      annual_turnover: details.annual_turnover ?? '',
      num_employees: details.num_employees != null ? String(details.num_employees) : '',
      website: details.website || '',
      pan_number: details.pan_number || '',
      aadhaar_number: details.aadhaar_number || '',
      gst_number: details.gst_number || '',
      passport_number: details.passport_number || '',
      driving_license: details.driving_license || '',
      address_line1: details.address_line1 || '',
      address_line2: details.address_line2 || '',
      landmark: details.landmark || '',
      city: lead.city_name || details.city_name || '',
      state: lead.state || '',
      pincode: lead.pincode || '',
      source: lead.source || '',
      campaign: lead.campaign || '',
      product_interest: lead.product_interest || '',
      estimated_premium: lead.estimated_premium || '',
      next_follow_up: toDateTimeLocal(lead.next_follow_up),
      follow_up_notes: lead.follow_up_notes || '',
      priority: (['low', 'medium', 'high', 'hot'] as const).includes(
        lead.priority as 'low' | 'medium' | 'high' | 'hot'
      )
        ? (lead.priority as 'low' | 'medium' | 'high' | 'hot')
        : 'medium',
      notes: lead.notes || '',
      registration_number: lead.registration_number || '',
      vehicle_type: lead.vehicle_type || '',
      vehicle_make: lead.vehicle_make || '',
      vehicle_model: lead.vehicle_model || '',
      vehicle_variant: lead.vehicle_variant || '',
      vehicle_fuel_type: lead.vehicle_fuel_type || '',
      manufacturing_year: lead.manufacturing_year ? String(lead.manufacturing_year) : '',
      is_new_vehicle:
        lead.is_new_vehicle === null || lead.is_new_vehicle === undefined
          ? ''
          : lead.is_new_vehicle
            ? 'yes'
            : 'no',
      existing_policy_expiry: lead.existing_policy_expiry || '',
    });
  }, [lead, reset]);

  const onSubmit = (data: LeadFormData) => {
    const contact_details: Record<string, unknown> = {};
    for (const key of CONTACT_DETAIL_KEYS) {
      const value = data[key as keyof LeadFormData];
      if (value === undefined || value === '' || value === null) continue;
      contact_details[key] = key === 'num_employees' ? Number(value) : value;
    }
    // Lead.city is a FK resolved by name server-side and silently dropped when
    // it matches no City row; keeping the typed text here means the address is
    // still complete on the Customer created at conversion.
    if (data.city?.trim()) contact_details.city_name = data.city.trim();

    const payload: LeadWritePayload = {
      salutation: data.salutation || undefined,
      // A corporate lead has no person's name, but Lead.first_name is the
      // display name -- mirror the company into it, as CustomerFormPage does.
      first_name:
        data.customer_type === 'corporate'
          ? (data.company_name || '').trim()
          : (data.first_name || '').trim(),
      last_name: data.last_name || '',
      email: data.email || undefined,
      phone: data.phone,
      alternate_phone: data.alternate_phone || undefined,
      whatsapp_number: data.whatsapp_number || undefined,
      company_name: data.company_name || undefined,
      designation: data.designation || undefined,
      industry: data.industry || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      pincode: data.pincode || undefined,
      source: data.source,
      campaign: data.campaign || undefined,
      product_interest: data.product_interest,
      estimated_premium: data.estimated_premium || undefined,
      priority: data.priority,
      // Item 9. Sent as `null`, never omitted, when the field is cleared:
      // this is a PATCH in edit mode, so omitting it would silently keep a
      // follow-up date the user has just deleted.
      next_follow_up: data.next_follow_up ? fromDateTimeLocal(data.next_follow_up) : null,
      follow_up_notes: data.follow_up_notes || '',
      notes: data.notes || undefined,
      contact_details: contact_details as LeadContactDetails,
      // Motor detail.
      //
      // When the lead is NOT motor the fields are sent EMPTY rather than
      // omitted. Omitting them looks equivalent but is not: this is a PATCH,
      // so a lead switched from Motor to Health would keep the vehicle it was
      // captured with, and that vehicle would then be carried onto a health
      // policy created from it.
      ...(data.product_interest === 'motor'
        ? {
            registration_number: data.registration_number || '',
            vehicle_type: data.vehicle_type || '',
            vehicle_make: data.vehicle_make || '',
            vehicle_model: data.vehicle_model || '',
            vehicle_variant: data.vehicle_variant || '',
            vehicle_fuel_type: data.vehicle_fuel_type || '',
            manufacturing_year: data.manufacturing_year
              ? Number(data.manufacturing_year)
              : null,
            is_new_vehicle:
              data.is_new_vehicle === 'yes'
                ? true
                : data.is_new_vehicle === 'no'
                  ? false
                  : null,
            existing_policy_expiry: data.existing_policy_expiry || null,
          }
        : {
            registration_number: '',
            vehicle_type: '',
            vehicle_make: '',
            vehicle_model: '',
            vehicle_variant: '',
            vehicle_fuel_type: '',
            manufacturing_year: null,
            is_new_vehicle: null,
            existing_policy_expiry: null,
          }),
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: payload }, { onSuccess: () => navigate(`/leads/${id}`) });
    } else {
      createMutation.mutate(payload, { onSuccess: (newLead) => navigate(`/leads/${newLead.id}`) });
    }
  };

  /** Render one field of the shared Contacts schema. */
  function renderSpec(spec: CustomerFieldSpec) {
    const name = spec.key as SchemaFieldName;
    const error = errors[name]?.message as string | undefined;
    // The lead form asks for less than the Contacts form: only the name that
    // identifies the lead is required (see superRefine). Everything else --
    // including last_name, which Contacts demands -- stays optional, because a
    // half-known lead must still be capturable.
    const required = spec.key === 'first_name' || spec.key === 'company_name' ? spec.required : false;

    if (spec.inputType === 'select') {
      return (
        <Select
          key={spec.key}
          label={spec.label}
          options={spec.options ?? []}
          placeholder="Select..."
          error={error}
          required={required}
          {...register(name)}
        />
      );
    }

    const isName = spec.key === 'first_name' || spec.key === 'middle_name' || spec.key === 'last_name';
    return (
      <Input
        key={spec.key}
        label={spec.label}
        type={spec.inputType === 'number' ? 'number' : spec.inputType === 'date' ? 'date' : spec.inputType === 'url' ? 'url' : 'text'}
        placeholder={spec.placeholder}
        maxLength={spec.maxLength}
        inputMode={spec.inputMode}
        min={spec.min}
        step={spec.step}
        error={error}
        required={required}
        {...register(name, {
          onChange: spec.digitsOnly ? digitsOnlyOnChange : isName ? nameKeepOnChange : undefined,
        })}
      />
    );
  }

  const basicSpecs = getBasicFieldSpecs(customerType);
  const identitySpecs = getIdentityFieldSpecs(customerType);

  if (isEditing && leadLoading) {
    return <ContentLoader />;
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/leads" />
      </div>
      <PageHeader
        title={isEditing ? 'Edit Lead' : 'Add Lead'}
        breadcrumbs={
          [
            { label: 'Leads', href: '/leads' },
            isEditing && lead ? { label: lead.full_name, href: `/leads/${id}` } : null,
            { label: isEditing ? 'Edit' : 'New' },
          ].filter(Boolean) as { label: string; href?: string }[]
        }
        actions={
          <Link to={isEditing ? `/leads/${id}` : '/leads'}>
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Customer Type -- drives every field set below it. */}
          <Card>
            <CardHeader
              title="Customer Type"
              subtitle="Captured now so conversion copies it straight onto the customer"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="Customer Type"
                options={customerTypeOptions}
                error={errors.customer_type?.message}
                required
                {...register('customer_type')}
              />
              <Select
                label="Segment"
                options={segmentOptions}
                placeholder="Select..."
                error={errors.segment?.message}
                {...register('segment')}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Basic Information" subtitle="The same fields the Contacts form asks for" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {basicSpecs.map(renderSpec)}
            </div>
          </Card>

          <Card>
            <CardHeader title="Contact" subtitle="How can we reach this lead?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="9999999999"
                error={errors.phone?.message}
                required
                {...register('phone', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="lead@example.com"
                error={errors.email?.message}
                {...register('email')}
              />
              <Input
                label="Alternate Phone"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                error={errors.alternate_phone?.message}
                {...register('alternate_phone', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="WhatsApp Number"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                error={errors.whatsapp_number?.message}
                {...register('whatsapp_number', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="Designation"
                error={errors.designation?.message}
                {...register('designation')}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Identity & Tax" subtitle="Optional now, required by most insurers later" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {identitySpecs.map(renderSpec)}
            </div>
          </Card>

          <Card>
            <CardHeader title="Address" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Address Line 1"
                error={errors.address_line1?.message}
                {...register('address_line1')}
              />
              <Input
                label="Address Line 2"
                error={errors.address_line2?.message}
                {...register('address_line2')}
              />
              <Input label="Landmark" error={errors.landmark?.message} {...register('landmark')} />
              {/* B6: PIN pre-fills State (and City); State is always a dropdown. */}
              <AddressFields
                pincode={watch('pincode') ?? ''}
                onPincodeChange={(value) => setValue('pincode', value, { shouldValidate: true, shouldDirty: true })}
                pincodeError={errors.pincode?.message}
                city={watch('city') ?? ''}
                onCityChange={(value) => setValue('city', value, { shouldValidate: true, shouldDirty: true })}
                cityError={errors.city?.message}
                state={watch('state') ?? ''}
                onStateChange={(value) => setValue('state', value, { shouldValidate: true, shouldDirty: true })}
                stateError={errors.state?.message}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Lead Details" subtitle="Source and potential value" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="Source"
                options={sourceOptions}
                placeholder="Select source"
                error={errors.source?.message}
                required
                {...register('source')}
              />
              <Select
                label="Priority"
                options={priorityOptions}
                error={errors.priority?.message}
                {...register('priority')}
              />
              <Select
                label="Product Interest"
                options={productInterestOptions}
                error={errors.product_interest?.message}
                required
                {...register('product_interest')}
              />
              <Input
                label="Estimated Premium"
                type="number"
                placeholder="0.00"
                error={errors.estimated_premium?.message}
                required={!isEditing}
                {...register('estimated_premium')}
              />
              <Input
                label="Next Follow-up"
                type="datetime-local"
                error={errors.next_follow_up?.message}
                {...register('next_follow_up')}
              />
            </div>
            <div className="mt-6">
              <label
                htmlFor="follow_up_notes"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Follow-up Remarks
              </label>
              <textarea
                id="follow_up_notes"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                rows={3}
                placeholder="What to say, or what was promised — e.g. call after 6pm, quote for a second car"
                {...register('follow_up_notes')}
              />
              {errors.follow_up_notes?.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.follow_up_notes.message}</p>
              )}
            </div>
          </Card>

          {/* Insurance-type-specific detail.
              A motor lead that records only a name and a phone number makes
              whoever picks it up ask the customer for the vehicle a second
              time, and makes the policy created from it a re-key. The columns
              already existed on the model; this is the form that fills them. */}
          {isMotorLead && (
            <Card>
              <CardHeader
                title="Vehicle Details"
                subtitle="Carried onto the quote or policy created from this lead"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Vehicle Number"
                  placeholder="e.g. MH01AB1234"
                  error={errors.registration_number?.message}
                  {...register('registration_number', { onChange: registrationOnChange })}
                />
                <Select
                  label="Vehicle Type"
                  options={vehicleTypeOptions}
                  error={errors.vehicle_type?.message}
                  {...register('vehicle_type')}
                />
                <Input
                  label="Make"
                  placeholder="e.g. Maruti Suzuki"
                  error={errors.vehicle_make?.message}
                  {...register('vehicle_make')}
                />
                <Input
                  label="Model"
                  placeholder="e.g. Swift"
                  error={errors.vehicle_model?.message}
                  {...register('vehicle_model')}
                />
                <Input
                  label="Variant"
                  placeholder="e.g. VXi"
                  error={errors.vehicle_variant?.message}
                  {...register('vehicle_variant')}
                />
                <Select
                  label="Fuel Type"
                  options={fuelTypeOptions}
                  error={errors.vehicle_fuel_type?.message}
                  {...register('vehicle_fuel_type')}
                />
                <Input
                  label="Manufacturing Year"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="e.g. 2021"
                  error={errors.manufacturing_year?.message}
                  {...register('manufacturing_year', { onChange: digitsOnlyOnChange })}
                />
                <Select
                  label="New Vehicle?"
                  options={newVehicleOptions}
                  error={errors.is_new_vehicle?.message}
                  {...register('is_new_vehicle')}
                />
                <Input
                  label="Existing Policy Expiry"
                  type="date"
                  error={errors.existing_policy_expiry?.message}
                  {...register('existing_policy_expiry')}
                />
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Additional Notes" />
            <textarea
              aria-label="Additional Notes"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              rows={4}
              placeholder="Any additional notes about this lead..."
              {...register('notes')}
            />
          </Card>

          <div className="flex justify-end gap-4">
            <Link to={isEditing ? `/leads/${id}` : '/leads'}>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {isEditing ? 'Update Lead' : 'Create Lead'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
